/**
 * Helpers PUROS da telemetria de retenção (docs/systems/retention-telemetry.md).
 * Sem dependência de Firebase/window — testáveis em node (padrão "core
 * testável" do projeto). O collector (telemetryCollector.ts) importa daqui.
 */

/** Allowlist v1 (spec §6). session_start é doc próprio (heartbeat), não evento. */
export const RETENTION_EVENT_ALLOWLIST: ReadonlySet<string> = new Set([
  "onboarding_completed",
  "shelf_import_done",
  "first_book_added",
  "first_progress_logged",
  // Ativação (07/10, `redesign-livra/ativacao/`): o livro de AGORA definido no
  // onboarding ou na Home. `where` (onboarding | home), `via` (busca | codigo |
  // populares), `format`. É o numerador da meta "Lendo nas 1ªs 24 h".
  "current_book_set",
  // Primeira semana (07/10, `redesign-livra/primeira-semana/`): o cartão "Dia 1
  // de leitura" — `action` (shown | done | not_now | backdrop | change_hour |
  // reminder_on | reminder_denied | reminder_no_token), `variant`, `hour`.
  "day_one_card",
  // Pré-cadastro "Sou autor" (A1a, docs/systems/author-application.md §10):
  // `action` (open | step2 | step3 | submit | edit | withdraw | account_too_new)
  // e `source` (ajustes | hub | landing | perfil). Sem PII: nunca o @ nem o nome.
  "author_apply",
  "focus_completed",
  "league_view",
  "store_view",
  "share_card_shared",
  "cover_scan_resolved",
  "match_computed",
  "affiliate_click",
  // Funil do convite de push (docs/rounds/2026-09.md, rodada de 11/09): os 5
  // já existiam via track() mas evaporavam — nada fica gravado hoje sobre
  // quantas pessoas veem a folha, e menos ainda sobre COMO saem dela (o que
  // impediu medir a hipótese do scrim/arrastar). props sem PII, como sempre.
  "push_prompt_shown",
  "push_prompt_dismissed",
  "push_prompt_enabled",
  "push_prompt_denied",
  "push_prompt_no_token",
  // F0 do Roadmap PRO (11/09): o funil de PRO inteiro tinha 3 chamadas de
  // track(), todas em ProLapseModal (perda do PRO) — nada entre "a conta
  // existe" e "pagou". Ver docs/rounds/2026-09.md.
  "pro_view",
  "pro_cta_click",
  "trial_started",
  "checkout_started",
  // Oferta do PRO no fim do onboarding (23/09, `features/pro/onboardingOffer.ts`):
  // a impressão da folha e a dispensa. Sem a impressão, "ninguém tocou" seria
  // indistinguível de "a folha nem abriu" (loja sem teste, gate ocupado).
  "pro_offer_view",
  "pro_offer_dismissed",
  // Divulgação do PRO (27/09, `docs/product/estudo-pro-divulgacao.md`): a
  // IMPRESSÃO de cada convite (`trackProNudgeView`, 1× por origem por sessão).
  // Sem ela, `pro_cta_click{origin}` não tem denominador.
  "pro_nudge_view",
  // Desfecho da compra (06/10, `features/pro/commerce/purchaseTelemetry.ts`):
  // o funil acabava no TOQUE (`checkout_started`); desistir na folha da loja e
  // a loja recusar eram indistinguíveis. Leva `outcome`, `plan`, `trial`, `os`.
  "purchase_result",
  // F0 do estudo de afiliados (17/09, `docs/product/estudo-afiliados-conversao.md`
  // §4.G): o funil de compra tinha SÓ o clique. Sem impressão nem abertura de
  // ficha não existe CTR, e "ninguém vê o botão" era indistinguível de "veem e
  // não querem". Os dois são 1× por ficha por sessão (`trackOncePerSession`).
  "book_detail_view",
  "affiliate_impression",
  // Import de estante (24/09, BACKLOG §0 item 5): 3 imports chegaram de um app
  // que não reconhecemos — autor vazio em 100% das 1.086 linhas — e NADA
  // guardava o cabeçalho do arquivo. Este evento já existia via track() e
  // evaporava; agora leva as colunas que não casaram (nomes de coluna, não
  // conteúdo — sem PII) e é o que vai dizer qual apelido falta.
  "shelf_import_enqueued",
  // Leitores parecidos (01/10, `docs/systems/people-suggestions.md` §8): todos
  // levam `door` (home · home_desktop · private_tab). A VISTA é o denominador
  // estrito do corte de 8%/3% (o servidor guarda a versão inflada);
  // `people_suggestion_open` é o 4º evento (Q14): o toque no cartão.
  "people_suggestions_view",
  "people_suggestion_follow",
  "people_club_click",
  "people_suggestion_open",
  // O × do cabeçalho (05/10, mock leitores-parecidos-v2 E7): esconde até a
  // lista nova. `undone` = tocou "Desfazer". Mede se a seção incomoda.
  "people_suggestions_hide",
  // O × de CADA cartão (15/10, §6 reaberto): uma pessoa sai até a lista nova.
  // Sem uid de ninguém: `kind`/`rank` dizem que tipo de sugestão incomoda.
  "people_suggestion_dismiss",
  // Convite pelo app (06/10, `docs/systems/league-invite-in-app.md` §6): a
  // folha aberta (denominador, com `count` = candidatos, em balde) e cada
  // envio com o `status` do servidor (sent · already_invited · recipient_busy
  // · erro). Sem uid de ninguém.
  "club_invite_open",
  "club_invite_send",
]);

export function isRetentionEvent(event: string): boolean {
  return RETENTION_EVENT_ALLOWLIST.has(event);
}

/** Dia BR "YYYY-MM-DD" no client (espelha dayKeyForBR do server p/ telemetria). */
export function dayKeyBRClient(d: Date = new Date()): string {
  return d
    .toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/")
    .reverse()
    .join("-");
}
